import {
  pgTable,
  uuid,
  text,
  timestamp,
  numeric,
  integer,
  boolean,
  jsonb,
  index,
  uniqueIndex,
  pgPolicy,
  check,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { appUserRole } from './enums.js';
import { tenants, users } from './users.js';
import { jobs } from './jobs.js';
import { disposalFacilities } from './disposals.js';
import { haulerDisposalFacilities, haulerLocations } from './platform-catalog.js';

/**
 * Phase 06.1 / DEC-027 — lifecycle event log for jobs.
 * Phase 06.2 (DEC-031 #4) additions: UNLOADING_START + DISPOSAL_OUT
 * extend the allowlist from 10 event_type literals to 12 event_type literals.
 *
 * job_events is a SEPARATE log dimension from the canonical 16-state §8 model
 * (jobs.status_id). Events record WHAT happened with device timestamp + GPS;
 * status_id tracks the canonical current state. Most events trigger
 * transitions; some are audit-only (SHIFT_STARTED, GAUGE_ENTRY_STARTED).
 *
 * D-14: job_events IS the lifecycle audit trail; audit_events covers
 * non-lifecycle audit (capture updates, signature/photo uploads, permission
 * denials, etc.). No double-write.
 *
 * D-06 idempotency: UNIQUE on (job_id, event_type, occurred_at) makes
 * duplicate sync replays a no-op via ON CONFLICT DO NOTHING.
 *
 * §16 GPS: (0,0) is the REJECTED sentinel — never stored. NULL is "no GPS
 * fix available" (legitimate; permission denied or 5s timeout exhausted).
 */
export const jobEvents = pgTable(
  'job_events',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    jobId: uuid('job_id')
      .notNull()
      .references(() => jobs.id, { onDelete: 'cascade' }),
    eventType: text('event_type').notNull(),
    // D-13: device time authoritative (matches DEC-004 local-by-context).
    occurredAt: timestamp('occurred_at', { withTimezone: true }).notNull(),
    // D-13: server-receive time per §16 — NEVER backdate.
    syncedAt: timestamp('synced_at', { withTimezone: true }).notNull().defaultNow(),
    // D-12: NULL when no GPS fix; (0,0) is the REJECTED sentinel per §16.
    gpsLat: numeric('gps_lat', { precision: 10, scale: 7 }),
    gpsLng: numeric('gps_lng', { precision: 10, scale: 7 }),
    gpsAccuracyFt: integer('gps_accuracy_ft'),
    capturedOffline: boolean('captured_offline').notNull().default(false),
    // D-09: actual_disposal_facility_id snapshot on ARRIVED_DISPOSAL / DISPOSAL_COMPLETE.
    facilityId: uuid('facility_id').references(() => disposalFacilities.id, {
      onDelete: 'restrict',
    }),
    // Phase 26 / Task 26.3 (DEC-147 #6) — dropoff-target audit snapshot when the
    // job drops at a hauler_location (can_dropoff=true) instead of a disposal facility.
    // Mirrors the jobs.actual_dropoff_hauler_location_id dual-FK; exactly one of
    // {facility_id, dropoff_hauler_location_id} is set for a dropoff-related event.
    dropoffHaulerLocationId: uuid('dropoff_hauler_location_id').references(
      () => haulerLocations.id,
      { onDelete: 'restrict' },
    ),
    // 0208 — the snapshot when the dropoff is an authorized disposal well (SWD destination
    // A1). At most one of {facility_id, dropoff_hauler_location_id, dropoff_disposal_well_id}.
    dropoffDisposalWellId: uuid('dropoff_disposal_well_id').references(
      () => haulerDisposalFacilities.id,
      { onDelete: 'restrict' },
    ),
    deviceId: uuid('device_id'),
    // NULLABLE — SYSTEM-actor events (JOB_AUTO_IN_REVIEW, per D-10/D-11) carry no
    // user. The API layer validates non-NULL on driver-initiated events
    // (Plan 06.1-08 / 06.1-09).
    actorUserId: uuid('actor_user_id').references(() => users.id, { onDelete: 'restrict' }),
    payload: jsonb('payload'),
    // Phase 8 / Task 8.4 — GPS verification result on arrival events (§15/§20).
    // NULL for non-arrival events and pre-8.4 rows.
    verificationResult: text('verification_result'),
    distanceFromCenter: numeric('distance_from_center', { precision: 10, scale: 2 }),
    verificationThresholdFt: integer('verification_threshold_ft'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    // D-06 idempotency — UNIQUE on (job_id, event_type, occurred_at).
    uniqueIndex('job_events_idempotency_idx').on(t.jobId, t.eventType, t.occurredAt),
    index('job_events_tenant_job_occurred_idx').on(t.tenantId, t.jobId, t.occurredAt),
    index('job_events_tenant_actor_occurred_idx').on(
      t.tenantId,
      t.actorUserId,
      t.occurredAt.desc(),
    ),
    index('job_events_tenant_type_occurred_idx').on(t.tenantId, t.eventType, t.occurredAt.desc()),
    // 0208 (#906 review) — the disposal-well snapshot lookup.
    index('job_events_dropoff_disposal_well_idx')
      .on(t.dropoffDisposalWellId)
      .where(sql`dropoff_disposal_well_id IS NOT NULL`),
    // 12 event_type literals — DEC-027 #1 + DISPOSAL-07 (stuck-job auto-IN_REVIEW)
    // + DEC-031 #4 (Phase 06.2: UNLOADING_START + DISPOSAL_OUT driver-fireable).
    // JOB_AUTO_IN_REVIEW is SYSTEM-only; no client can trigger it.
    check(
      'job_events_type_check',
      sql`event_type IN (
        'JOB_ACCEPTED','EN_ROUTE_TO_PICKUP','ARRIVED_PICKUP',
        'GAUGE_ENTRY_STARTED','GAUGE_ENTRY_SAVED','DEPARTED_PICKUP',
        'ARRIVED_DISPOSAL','DISPOSAL_COMPLETE','SHIFT_STARTED',
        'JOB_AUTO_IN_REVIEW',
        'UNLOADING_START','DISPOSAL_OUT'
      )`,
    ),
    // §16 GPS (0,0) is the REJECTED sentinel — never stored.
    check('job_events_gps_zero_zero_check', sql`NOT (gps_lat = 0 AND gps_lng = 0)`),
    // Phase 26 / Task 26.3 — at-most-one dropoff-target snapshot (mirrors the
    // jobs table's dropoff-target-at-most-one invariant).
    check(
      'job_events_dropoff_target_at_most_one',
      sql`num_nonnulls(facility_id, dropoff_hauler_location_id, dropoff_disposal_well_id) <= 1`,
    ),
    // Phase 8 / Task 8.4 — verification result allowlist (§15/§20).
    check(
      'job_events_verification_result_check',
      sql`verification_result IS NULL OR verification_result IN
        ('VERIFIED','OUTSIDE_RANGE','UNVERIFIABLE','NO_GPS')`,
    ),
    pgPolicy('job_events_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type JobEvent = typeof jobEvents.$inferSelect;
export type NewJobEvent = typeof jobEvents.$inferInsert;
export const jobEventSelectSchema = createSelectSchema(jobEvents);
export const jobEventInsertSchema = createInsertSchema(jobEvents);
